import type {
	ApiRegistry,
	AnyRoute,
	InferInput,
	InferOutput,
	ApiResponse,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Client-side: createApiClient
//
// Generates a Proxy-based client where each API key becomes an object with
// .useQuery() / .useMutation() hooks (TanStack Query) and a .fetch() method.
//
// Import from '@fliisi/trellis/client'
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Fetch layer (framework-agnostic, no React dependency)
// ---------------------------------------------------------------------------

export interface TrellisClientConfig {
	/** Base URL for edge functions, e.g. 'https://xxx.supabase.co/functions/v1' */
	baseUrl: string;
	/** Optional: custom headers to include in every request */
	headers?: Record<string, string> | (() => Record<string, string>);
}

/**
 * Raw fetch for a trellis route. No React, no hooks — just typed fetch.
 * All requests are POST with JSON body.
 */
async function trellisCall<O>(
	config: TrellisClientConfig,
	functionName: string,
	input: unknown,
): Promise<O> {
	const baseHeaders =
		typeof config.headers === "function"
			? config.headers()
			: config.headers ?? {};

	const url = `${config.baseUrl}/${functionName}`;
	const res = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...baseHeaders,
		},
		body: JSON.stringify(input),
	});

	// Parse response — guard against non-JSON gateway errors (e.g. Supabase 401)
	let json: ApiResponse<O>
	try {
		json = await res.json()
	} catch {
		throw new TrellisError(
			`HTTP_${res.status}`,
			`${functionName} returned ${res.status} ${res.statusText} (non-JSON body)`,
		)
	}

	if (!res.ok || json.error) {
		const err = json.error
			? new TrellisError(json.error.code, json.error.message, json.error.issues)
			: new TrellisError(`HTTP_${res.status}`, `${functionName} returned ${res.status}`)
		throw err
	}

	return json.data as O;
}

// ---------------------------------------------------------------------------
// Error class
// ---------------------------------------------------------------------------

export class TrellisError extends Error {
	constructor(
		public readonly code: string,
		message: string,
		public readonly issues?: Array<{ path: (string | number)[]; message: string }>,
	) {
		super(message);
		this.name = "TrellisError";
	}
}

// ---------------------------------------------------------------------------
// Client types — what the consumer sees
// ---------------------------------------------------------------------------

/** A query endpoint exposes useQuery + fetch */
interface QueryEndpoint<I, O> {
	/** TanStack Query hook — call inside a React component */
	useQuery: (
		input: I,
		options?: {
			enabled?: boolean;
			staleTime?: number;
			refetchInterval?: number;
			refetchOnWindowFocus?: boolean;
		},
	) => { data: O | undefined; isLoading: boolean; error: TrellisError | null; refetch: () => void };

	/** Direct fetch — call anywhere (not a hook) */
	fetch: (input: I) => Promise<O>;
}

/** A mutation endpoint exposes useMutation + fetch */
interface MutationEndpoint<I, O> {
	/** TanStack Query hook — call inside a React component */
	useMutation: (options?: {
		onSuccess?: (data: O, input: I) => void;
		onError?: (error: TrellisError, input: I) => void;
	}) => {
		mutate: (input: I) => void;
		mutateAsync: (input: I) => Promise<O>;
		data: O | undefined;
		isLoading: boolean;
		error: TrellisError | null;
	};

	/** Direct fetch — call anywhere (not a hook) */
	fetch: (input: I) => Promise<O>;
}

/** Map a route to its endpoint shape */
type RouteEndpoint<R extends AnyRoute> = R extends { _type: "query" }
	? QueryEndpoint<InferInput<R>, InferOutput<R>>
	: R extends { _type: "mutation" }
		? MutationEndpoint<InferInput<R>, InferOutput<R>>
		: never;

/** The full typed client */
export type TrellisClient<T extends ApiRegistry> = {
	[K in keyof T]: RouteEndpoint<T[K]>;
};

// ---------------------------------------------------------------------------
// camelCase to kebab-case utility
// ---------------------------------------------------------------------------

function toKebab(str: string): string {
	// Already kebab-case or has no uppercase
	if (str.includes("-") || str === str.toLowerCase()) return str;
	return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

// ---------------------------------------------------------------------------
// createApiClient — the main entry point for consumers
// ---------------------------------------------------------------------------

/**
 * Creates a typed API client from your registry type.
 *
 * @example
 * ```ts
 * import { createApiClient } from '@fliisi/trellis/client'
 * import type { Api } from './registry'
 *
 * export const api = createApiClient<Api>({
 *   baseUrl: 'https://xxx.supabase.co/functions/v1',
 *   headers: () => ({
 *     Authorization: `Bearer ${getToken()}`,
 *     apikey: SUPABASE_ANON_KEY,
 *   }),
 * })
 *
 * // In a component:
 * const { data } = api.storeGet.useQuery({ id: '...' })
 * ```
 */
export function createApiClient<T extends ApiRegistry>(
	config: TrellisClientConfig,
): TrellisClient<T> {
	// Lazy-import TanStack Query to keep it optional
	let reactQuery: typeof import("@tanstack/react-query") | null = null;
	let reactQueryLoaded = false;

	function getReactQuery() {
		if (!reactQueryLoaded) {
			try {
				// Dynamic require to keep @tanstack/react-query optional.
				// Uses Function constructor to avoid static analysis / bundler issues.
				const dynamicRequire = new Function("id", "return require(id)") as (id: string) => any;
				reactQuery = dynamicRequire("@tanstack/react-query");
			} catch {
				reactQuery = null;
			}
			reactQueryLoaded = true;
		}
		return reactQuery;
	}

	// Each property access on the client returns an endpoint Proxy
	return new Proxy({} as TrellisClient<T>, {
		get(_target, prop: string) {
			const functionName = toKebab(prop);

			return {
				fetch: (input: unknown) =>
					trellisCall(config, functionName, input),

				useQuery: (input: unknown, options?: Record<string, unknown>) => {
					const rq = getReactQuery();
					if (!rq) {
						throw new Error(
							"@tanstack/react-query is required for useQuery. Install it or use .fetch() instead.",
						);
					}
					return rq.useQuery({
						queryKey: [functionName, input],
						queryFn: () => trellisCall(config, functionName, input),
						...options,
					});
				},

				useMutation: (options?: Record<string, unknown>) => {
					const rq = getReactQuery();
					if (!rq) {
						throw new Error(
							"@tanstack/react-query is required for useMutation. Install it or use .fetch() instead.",
						);
					}
					return rq.useMutation({
						mutationFn: (input: unknown) =>
							trellisCall(config, functionName, input),
						...options,
					});
				},
			};
		},
	});
}
